import React, { useState, useMemo } from 'react';
import searchIcon from '../../assets/images/search-icon.png';
import emptyImg from '../../assets/images/empty.png';
import {
    DOMAIN_FILTERS,
    DOMAIN_TAG_COLOR,
    resolveTagDomain,
} from '../../config/data';
import { usePage, PAGES } from '../../context/PageContext';
import { t } from '../../utils/i18n';
import './index.css';

export default function ArticleList({
    pageKey,
    title,
    icon,
    data,
    enableDomainFilter = true,
}) {
    const { navigate } = usePage();
    const [search, setSearch] = useState('');
    const [activeDomain, setActiveDomain] = useState('all');

    const filtered = useMemo(() => {
        let list = data ?? [];
        if (enableDomainFilter && activeDomain !== 'all') {
            list = list.filter((item) => resolveTagDomain(item.tag) === activeDomain);
        }
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            list = list.filter(
                (item) =>
                    item.title?.toLowerCase().includes(q) ||
                    item.tag?.toLowerCase().includes(q)
            );
        }
        // 按日期降序排列，无日期排末尾
        list = [...list].sort((a, b) => {
            if (!a.date && !b.date) return 0;
            if (!a.date) return 1;
            if (!b.date) return -1;
            return (b.date || '').localeCompare(a.date || '');
        });
        return list;
    }, [data, search, activeDomain, enableDomainFilter]);

    return (
        <div className="article-list-wrapper" key={pageKey}>
            <div className="article-list-header">
                <div className="article-list-header-row">
                    <div className="article-list-header-left">
                        {icon && <img src={icon} className="article-list-icon" alt="" />}
                        <span className="article-list-title">{title}</span>
                    </div>
                    <div className="article-list-header-right">
                        {enableDomainFilter && (
                            <div className="article-list-domain-filters" role="tablist" aria-label="按研究域过滤">
                                {DOMAIN_FILTERS.map((f) => (
                                    <button
                                        key={f.id}
                                        type="button"
                                        role="tab"
                                        aria-selected={activeDomain === f.id}
                                        className={`article-list-domain-chip${activeDomain === f.id ? ' is-active' : ''}`}
                                        onClick={() => setActiveDomain(f.id)}
                                    >
                                        {f.label}
                                    </button>
                                ))}
                            </div>
                        )}
                        <div className="article-list-search-wrap">
                            <input
                                className="article-list-search"
                                placeholder={t('搜索标题 / 标签', 'Search title / tag')}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            <img className="article-list-search-icon" src={searchIcon} alt="" />
                        </div>
                    </div>
                </div>
                <div className="article-list-header-divider" />
            </div>
            <div className="article-list-body">
                {filtered.length === 0 ? (
                    <div className="article-list-empty">
                        <img className="article-list-empty-img" src={emptyImg} alt="" />
                        <div className="article-list-empty-text">{t('暂无数据', 'No data')}</div>
                    </div>
                ) : (
                    filtered.map((item, idx) => {
                        const hasHref = !!item.href;
                        const hasInternalPage = !hasHref && !!item.id;
                        const isClickable = hasHref || hasInternalPage;
                        const domain = resolveTagDomain(item.tag);
                        const colorClass = DOMAIN_TAG_COLOR[domain] || 'tag-neutral';
                        return (
                            <div
                                className={`article-list-item${isClickable ? ' is-clickable' : ''}`}
                                key={item.id ?? idx}
                                onClick={() => {
                                    if (hasHref) {
                                        window.open(item.href, '_blank');
                                    } else if (hasInternalPage) {
                                        navigate(PAGES.ARTICLE_DETAIL, { articleId: item.id });
                                    }
                                }}
                            >
                                <div className="article-list-item-row">
                                    <span className={`article-list-category ${colorClass}`}>{item.tag}</span>
                                    <span className="article-list-item-title">{item.title}</span>
                                </div>
                                {item.desc && (
                                    <div className="article-list-item-desc">{item.desc}</div>
                                )}
                                {idx < filtered.length - 1 && <div className="article-list-item-divider" />}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
